import type { WorkflowState } from '../../../store/redux/state';
import type { Change } from '../types';
import { Operation } from '../types';
import {
  getFirstSnippetParentId,
  isInternalSnippetChildAction,
} from '../../actions/snippets/snippets';
import { isActionOfType } from 'flow/service/actions';
import { ActionType } from 'flow/store/engine/constants';
import type { Action, Threshold, SchemaInstance } from 'flow/store/engine/types';
import { isSnippetChildAction, pruneGateRule } from 'flow/service/actions/snippets';

/**
 * Returns a text representation of the Operation in the past tense
 * @param operation operation
 * @returns
 */
export function getOperationVerbInPastTense(operation: Operation) {
  const verb = {
    [Operation.Add]: 'added',
    [Operation.Modify]: 'modified',
    [Operation.Remove]: 'deleted',
  }[operation];

  if (!verb) {
    throw new TypeError(`The specified operation ${operation} does not exist`);
  }

  return verb;
}

/**
 * Returns the Operation contained in a Change within the `changes` array that best
 * summarizes the changes.
 * @param changes array of Change objects
 * @returns the Operation that best summarizes the list of changes
 */
export function synthesizeSingleOperationForChanges(changes?: Change[]): Operation {
  const hasAddActionOperation = changes?.some(({ descriptor, op }) => (
    op === Operation.Add && descriptor === 'action'));

  const hasRemoveActionOperation = changes?.some(({ descriptor, op }) => (
    op === Operation.Remove && descriptor === 'action'));

  if (hasAddActionOperation) {
    return Operation.Add;
  }

  if (hasRemoveActionOperation) {
    return Operation.Remove;
  }

  return Operation.Modify;
}


export function getRelevantVersionForOp(args: {
  versions: {
    versionA: WorkflowState['workflowData'];
    versionB: WorkflowState['workflowData'];
  };
  operation: Operation;
}) {
  const { operation, versions: { versionA, versionB } } = args;
  const operationVersionMap = {
    [Operation.Add]: versionB,
    [Operation.Modify]: versionB,
    [Operation.Remove]: versionA,
  };

  return operationVersionMap[operation];
}


export function getRelevantWorkflowPropsForOp(args: {
  workflowProps: {
    propsB: WorkflowState['workflowProps'];
    propsA: WorkflowState['workflowProps'];
  };
  operation: Operation;
}) {
  const { operation, workflowProps: { propsB, propsA } } = args;
  const operationVersionMap = {
    [Operation.Add]: propsB,
    [Operation.Modify]: propsB,
    [Operation.Remove]: propsA,
  };

  return operationVersionMap[operation];
}


export function getUniqueActionPublicIds(changes: Change[]) {
  return changes.reduce((ids, change) => ids.add(change.actionPublicId), new Set<string>());
}


/**
 * Returns the action to use when deciding whether an action has rules for changelog display.
 * For snippet child actions, gate rules are pruned.
 */
export function getActionWithChangelogRules(args: {
  action: Action;
  thresholds: Record<string, Threshold> | undefined;
}): Action {
  const { action, thresholds } = args;
  if (isSnippetChildAction(action)) {
    const prunedRules = pruneGateRule({ snippetAction: action, thresholds });
    return { ...action, rules: prunedRules };
  }
  return action;
}


/**
 * Returns display order for changelog formatters so that internal
 * snippet children are rendered under their snippet parent. Includes snippet parents
 * in the section set even when only their children have changes.
 */
export function getChangelogDisplayOrder(args: {
  changes: Change[];
  workflowVersions: {
    versionA: Partial<SchemaInstance>;
    versionB: Partial<SchemaInstance>;
  };
}): string[] {
  const { changes, workflowVersions: { versionA, versionB } } = args;

  const snippetActionSectionIds = new Map<string, Set<string>>(); // Map of snippet parent id to IDs of children (includes the parent id)
  const nonSnippetActionSectionIds = new Set<string>();
  changes.forEach(change => {
    const { actionPublicId } = change;
    const action = versionB.actions?.[actionPublicId] ?? versionA.actions?.[actionPublicId];

    if (isInternalSnippetChildAction(action)) {
      const snippetParentId = getFirstSnippetParentId(action.containerContext);
      let sectionSet = snippetActionSectionIds.get(snippetParentId);
      if (!sectionSet) {
        sectionSet = new Set([snippetParentId]);
        snippetActionSectionIds.set(snippetParentId, sectionSet);
      }
      sectionSet.add(actionPublicId);
    } else if (!isActionOfType(action, ActionType.SNIPPET)) {
      nonSnippetActionSectionIds.add(actionPublicId);
    }
  });

  // Get flat array of snippet action section ids (includes the parent id)
  const snippetActionSectionIdsArray = Array.from(snippetActionSectionIds.values())
    .flatMap(children => [...children]);

  const displayOrder: string[] = Array.from(nonSnippetActionSectionIds).concat(
    snippetActionSectionIdsArray,
  );

  return displayOrder;
}
